import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type BootstrapDTO, type MeDTO, type SearchResultDTO, THEMES } from "@justone/shared";
import { errorMessage, http } from "../api/http";
import { keys, useChannels, useConversations, useMe, useStartConversation, useUserMap, useUsers } from "../api/queries";
import { useFilters, useLists, useQuietHours } from "../api/tasks";
import { useWikiSpaces, useWikiTitles } from "../api/wiki";
import { isChoseongOnly, matchScore } from "../lib/match";
import { dmTitle } from "../lib/room";
import { applyTheme } from "../lib/theme";
import { wikiPagePath } from "../lib/wiki";
import { Avatar, Dialog, useToast } from "./ui";

interface Item {
  id: string;
  group: "최근" | "동작" | "이동" | "채널" | "DM" | "사람" | "위키" | "할일" | "테마" | "검색";
  label: string;
  hint?: string;
  icon?: ReactNode;
  /** 최근 목록에 남길 이동 (동작은 남기지 않는다) */
  path?: string;
  run: () => void;
}

const RECENT_KEY = "justone:palette-recent";
type Recent = { id: string; label: string; path: string; group: Item["group"] };

function loadRecent(): Recent[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as Recent[];
  } catch {
    return [];
  }
}
function saveRecent(r: Recent) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify([r, ...loadRecent().filter((x) => x.id !== r.id)].slice(0, 6)));
  } catch {
    // 저장소를 못 쓰면 최근 목록 없이
  }
}

export interface PaletteActions {
  quickAdd: () => void;
  createChannel: () => void;
  shortcuts: () => void;
}

/**
 * ⌘K 명령 팔레트: 채널·DM·사람·위키·할일로 바로 가고, 할일 추가 같은 동작을 실행한다.
 * 맨 아래 "전체 검색"은 검색 화면으로.
 */
export function CommandPalette({ open, onClose, actions }: { open: boolean; onClose: () => void; actions: PaletteActions }) {
  return open ? <PaletteBody onClose={onClose} actions={actions} /> : null;
}

function PaletteBody({ onClose, actions }: { onClose: () => void; actions: PaletteActions }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe();
  const users = useUserMap();
  const { data: allUsers = [] } = useUsers();
  const { data: channels = [] } = useChannels();
  const { data: conversations = [] } = useConversations();
  const { data: titles = [] } = useWikiTitles();
  const { data: spaces = [] } = useWikiSpaces();
  const { data: lists = [] } = useLists();
  const { data: filters = [] } = useFilters();
  const quiet = useQuietHours();
  const startDm = useStartConversation();
  const qc = useQueryClient();
  const q = query.trim();

  // 할일은 서버에서 찾는다 (잠깐 멈췄을 때)
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 200);
    return () => clearTimeout(t);
  }, [q]);
  const tasks = useQuery({
    queryKey: ["palette-tasks", debounced],
    queryFn: () => http.get<SearchResultDTO>(`/api/search?type=task&q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length > 0 && !isChoseongOnly(debounced),
    staleTime: 10_000,
  });

  const items = useMemo(() => {
    const go = (path: string) => () => navigate(path);
    const nav: Item[] = [
      { id: "p:inbox", group: "이동", label: "받은함", path: "/inbox", run: go("/inbox") },
      { id: "p:today", group: "이동", label: "오늘", path: "/today", run: go("/today") },
      { id: "p:upcoming", group: "이동", label: "다가오는", path: "/upcoming", run: go("/upcoming") },
      { id: "p:wiki", group: "이동", label: "공통 위키", path: "/wiki", run: go("/wiki") },
      { id: "p:threads", group: "이동", label: "스레드", path: "/threads", run: go("/threads") },
      { id: "p:drafts", group: "이동", label: "초안·예약", path: "/drafts", run: go("/drafts") },
      { id: "p:notifications", group: "이동", label: "알림", path: "/notifications", run: go("/notifications") },
      { id: "p:labels", group: "이동", label: "라벨", path: "/labels", run: go("/labels") },
      { id: "p:browse", group: "이동", label: "채널 둘러보기", path: "/channels", run: go("/channels") },
      { id: "p:settings", group: "이동", label: "내 계정", path: "/settings", run: go("/settings") },
      ...lists.map((l) => ({ id: `l:${l.id}`, group: "이동" as const, label: `목록 ${l.name}`, path: `/lists/${l.id}`, run: go(`/lists/${l.id}`) })),
      ...filters.map((f) => ({ id: `f:${f.id}`, group: "이동" as const, label: `필터 ${f.name}`, path: `/filters/${f.id}`, run: go(`/filters/${f.id}`) })),
    ];
    const act: Item[] = [
      { id: "a:task", group: "동작", label: "할일 추가", hint: "Q", run: actions.quickAdd },
      { id: "a:wiki", group: "동작", label: "새 위키 페이지 (공통)", run: go("/wiki?new=1") },
      { id: "a:channel", group: "동작", label: "채널 만들기", run: actions.createChannel },
      {
        id: "a:quiet",
        group: "동작",
        label: me.quietHours ? "방해 금지 끄기" : "방해 금지 켜기 (22:00~08:00)",
        run: () =>
          quiet.mutate(me.quietHours ? null : { start: "22:00", end: "08:00" }, {
            onSuccess: (m) => toast(m.quietHours ? `방해 금지: ${m.quietHours.start}~${m.quietHours.end}` : "방해 금지를 껐습니다."),
            onError: (e) => toast(errorMessage(e)),
          }),
      },
      { id: "a:keys", group: "동작", label: "단축키 보기", hint: "?", run: actions.shortcuts },
    ];
    const chans: Item[] = channels
      .filter((c) => c.joined || q)
      .map((c) => ({ id: `c:${c.id}`, group: "채널", label: `#${c.name}`, hint: c.joined ? (c.unread ? `안 읽음 ${c.unread}` : undefined) : "참여 안 함", path: `/c/${c.id}`, run: go(`/c/${c.id}`) }));
    const dms: Item[] = conversations.map((c) => ({ id: `d:${c.id}`, group: "DM", label: dmTitle(c, users, me.id), hint: c.unread ? `안 읽음 ${c.unread}` : undefined, path: `/d/${c.id}`, run: go(`/d/${c.id}`) }));
    const people: Item[] = allUsers
      .filter((u) => u.active && u.id !== me.id)
      .map((u) => ({
        id: `u:${u.id}`,
        group: "사람",
        label: u.displayName,
        hint: u.username ? `@${u.username}` : undefined,
        icon: <Avatar user={u} size={20} presence />,
        run: () => startDm.mutate([u.id], { onSuccess: (c) => navigate(`/d/${c.id}`), onError: (e) => toast(errorMessage(e)) }),
      }));
    // 화면 테마: 고르면 바로 바뀌고 계정에 저장
    const themes: Item[] = THEMES.map((t) => ({
      id: `th:${t.id}`,
      group: "테마",
      label: `테마: ${t.name}`,
      hint: t.id === me.preferences.theme ? "지금 테마" : t.note,
      run: () => {
        applyTheme(t.id);
        http
          .put<MeDTO>("/api/me/preferences", { theme: t.id })
          .then((next) => qc.setQueryData<BootstrapDTO>(keys.bootstrap, (b) => (b ? { ...b, me: next } : b)))
          .catch((e) => toast(errorMessage(e)));
      },
    }));
    const spaceName = (id: number) => spaces.find((s) => s.id === id)?.name ?? "위키";
    const wiki: Item[] = titles.map((t) => {
      const channelId = spaces.find((s) => s.id === t.spaceId)?.channelId ?? null;
      const path = wikiPagePath(channelId, t.id);
      return { id: `w:${t.id}`, group: "위키", label: t.title, hint: `${spaceName(t.spaceId)}${t.status === "draft" ? " · 초안" : ""}`, path, run: go(path) };
    });

    if (!q) {
      const byId = new Map([...nav, ...chans, ...dms, ...wiki].map((i) => [i.id, i]));
      const recent: Item[] = loadRecent()
        .map((r) => byId.get(r.id))
        .filter((i): i is Item => !!i)
        .map((i) => ({ ...i, group: "최근" }));
      return [...recent, ...act, ...nav.slice(0, 6)];
    }

    // 맞는 것만, 맞는 정도 순 (같으면 무리 순서)
    const ranked = [...act, ...chans, ...dms, ...people, ...wiki, ...nav, ...themes]
      .map((i) => ({ i, score: Math.max(matchScore(i.label, q), i.hint ? matchScore(i.hint, q) - 1 : 0) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12)
      .map((x) => x.i);
    // 가장 잘 맞는 무리부터, 같은 무리는 모아서
    const groupOrder = [...new Set(ranked.map((i) => i.group))];
    ranked.sort((a, b) => groupOrder.indexOf(a.group) - groupOrder.indexOf(b.group));
    const taskItems: Item[] = (debounced === q ? (tasks.data?.tasks.items ?? []) : []).slice(0, 5).map((t) => ({
      id: `t:${t.id}`,
      group: "할일",
      label: t.title,
      hint: `T-${t.id}${t.status === "done" ? " · 완료" : ""}`,
      run: () => navigate({ search: `?task=${t.id}` }),
    }));
    const search: Item = { id: "s:all", group: "검색", label: `"${q}" 전체 검색`, hint: "메시지·할일·위키·파일", run: go(`/search?q=${encodeURIComponent(q)}`) };
    return [...ranked, ...taskItems, search];
  }, [q, debounced, tasks.data, channels, conversations, allUsers, titles, spaces, lists, filters, users, me, actions, navigate, quiet, startDm, toast, qc]);

  // 고른 항목은 id로 기억한다 (할일 결과가 늦게 와도 고른 것이 바뀌지 않게)
  const active = items.find((i) => i.id === selected) ?? items[0];
  useEffect(() => {
    listRef.current?.querySelector(`[data-id="${CSS.escape(active?.id ?? "")}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active?.id]);

  const choose = (item: Item) => {
    if (item.path) saveRecent({ id: item.id, label: item.label, path: item.path, group: item.group });
    onClose();
    item.run();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    const i = active ? items.indexOf(active) : -1;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length];
      if (next) setSelected(next.id);
    } else if (e.key === "Enter" && active) {
      e.preventDefault();
      choose(active);
    }
  };

  let lastGroup = "";
  return (
    <Dialog open onClose={onClose} label="명령 팔레트" className="palette">
      <div className="palette-box">
        <input
          className="palette-input"
          role="combobox"
          aria-expanded="true"
          aria-controls="palette-list"
          aria-activedescendant={active ? `palette-${active.id}` : undefined}
          aria-label="어디로 갈지, 무엇을 할지"
          placeholder="채널·사람·위키·할일 이름, 또는 할 일 (예: 할일 추가)"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected(null);
          }}
          onKeyDown={onKeyDown}
          autoFocus
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="go"
        />
        <ul id="palette-list" ref={listRef} className="palette-list" role="listbox" aria-label="결과">
          {items.map((item) => {
            const head = item.group !== lastGroup ? item.group : null;
            lastGroup = item.group;
            return (
              <li key={item.id} role="presentation">
                {head && (
                  <div className="palette-group" role="presentation">
                    {head}
                  </div>
                )}
                <div
                  id={`palette-${item.id}`}
                  data-id={item.id}
                  role="option"
                  aria-selected={item === active}
                  className={`palette-item${item === active ? " on" : ""}`}
                  onPointerMove={() => item !== active && setSelected(item.id)}
                  onClick={() => choose(item)}
                >
                  {item.icon}
                  <span className="pi-label">{item.label}</span>
                  {item.hint && <span className="pi-hint">{item.hint}</span>}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="palette-foot" aria-hidden="true">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> 고르기
          </span>
          <span>
            <kbd>Enter</kbd> 열기
          </span>
          <span>
            <kbd>Esc</kbd> 닫기
          </span>
          <span>ㄱㅈ처럼 초성으로도 찾습니다</span>
        </div>
      </div>
    </Dialog>
  );
}
